import { Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CartItemSummary, CartSummary } from '../../../services/cart.service';

interface CartConfirmationState {
  cart: CartSummary;
  requestStatus?: string;
  requestStatuses?: string[];
}

@Component({
  selector: 'app-cart-confirmation',
  templateUrl: './cart-confirmation.component.html',
  styleUrls: ['./cart-confirmation.component.scss']
})
export class CartConfirmationComponent implements OnInit {
  cartSummary: CartSummary | null = null;
  requestStatus = 'pending';
  requestStatuses: string[] = [];

  constructor(
    private readonly router: Router,
    private readonly location: Location
  ) {}

  ngOnInit(): void {
    const navigationState = this.router.getCurrentNavigation()?.extras?.state as CartConfirmationState | undefined;
    const state = navigationState ?? (this.location.getState() as CartConfirmationState | undefined);
    if (state?.cart) {
      this.cartSummary = state.cart;
      this.requestStatuses = Array.isArray(state.requestStatuses) ? state.requestStatuses : [];
      this.requestStatus = state.requestStatus || this.resolveRequestStatus(this.requestStatuses);
    } else {
      void this.router.navigate(['/cart']);
    }
  }

  get itemCount(): number {
    if (!this.cartSummary?.items?.length) {
      return 0;
    }

    return this.cartSummary.items.reduce((total, item) => total + this.getQuantity(item), 0);
  }

  get items(): CartItemSummary[] {
    return this.cartSummary?.items ?? [];
  }

  formatCartTotal(): string {
    if (!this.cartSummary) {
      return '';
    }
    const total = this.cartSummary.totals?.['total'];
    const currency = this.cartSummary.totals?.['currency'] || this.cartSummary.currency;
    if (typeof total !== 'number' || !currency) {
      return '';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency as string
    }).format(total);
  }

  formatPop(item: CartItemSummary): string {
    const displayId = item.payload?.pop?.displayId || item.payload?.pop?.name || '';
    const technicalPop = item.payload?.connectionPoint?.crossConnect?.technicalPop?.technicalId || '';
    if (displayId && technicalPop) {
      return `${displayId} (${technicalPop})`;
    }
    return displayId || technicalPop || 'PoP';
  }

  formatItemTitle(item: CartItemSummary): string {
    const companyName = this.getCompanyName(item);
    const prefix = companyName ? `Extra XCO (ADDP) for ${companyName}` : 'ADDP';
    const pop = this.formatPop(item);
    return pop ? `${prefix} · ${pop}` : prefix;
  }

  formatProductList(item: CartItemSummary): string {
    const products = item.payload?.items || [];
    if (!Array.isArray(products) || products.length === 0) {
      return 'No product selected';
    }
    return products
      .map((entry: any) => entry?.name || entry?.product?.name)
      .filter(Boolean)
      .join(', ');
  }

  getQuantity(item: CartItemSummary): number {
    const quantity = Number(item.payload?.quantity ?? 1);
    if (!Number.isFinite(quantity)) {
      return 1;
    }

    return Math.max(1, Math.trunc(quantity));
  }

  getTechnicalPopName(item: CartItemSummary): string | null {
    const name = item.payload?.connectionPoint?.crossConnect?.technicalPop?.name;
    if (typeof name === 'string' && name.trim().length > 0) {
      return name.trim();
    }
    return null;
  }

  getOriginalXco(item: CartItemSummary): string | null {
    const xco = item.payload?.xco;
    if (typeof xco === 'string' && xco.trim().length > 0) {
      return xco.trim();
    }
    return null;
  }

  trackByItem(_index: number, item: CartItemSummary): string {
    return item.uuid;
  }

  private resolveRequestStatus(statuses: string[]): string {
    if (!Array.isArray(statuses) || statuses.length === 0) {
      return 'pending';
    }
    return statuses[0] || 'pending';
  }

  private getCompanyName(item: CartItemSummary): string | null {
    const name = item.payload?.company?.name;
    if (typeof name === 'string' && name.trim().length > 0) {
      return name.trim();
    }
    return null;
  }
}
